// Panel de facturación electrónica dentro del detalle de una venta:
// muestra el estado del documento ante la DIAN y permite emitirlo o reintentarlo.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  ReceiptLong as ReceiptIcon, CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon, PictureAsPdf as PdfIcon, Code as XmlIcon,
  Send as SendIcon, Refresh as RetryIcon,
} from '@mui/icons-material';
import { toast } from '../../../utils/toast';
import { cufeLegible } from '../../../utils/dian';
import {
  DocumentoElectronico, FacturacionElectronicaApi,
} from '../../../services/api/facturacionElectronicaApi';

interface Props {
  idVenta: number;
  /** Numero de la venta; las temporales (TEMP-) no son facturables. */
  numeroFactura?: string;
  /** Avisa al contenedor cuando cambia el documento (para refrescar impresiones). */
  onCambio?: (doc: DocumentoElectronico | null) => void;
}

const COLOR_ESTADO: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  VALIDADO: 'success',
  RECHAZADO: 'error',
  ERROR: 'error',
  ENVIADO: 'warning',
  PENDIENTE: 'default',
};

export const PanelDocumentoDian: React.FC<Props> = ({ idVenta, numeroFactura, onCambio }) => {
  const [doc, setDoc] = useState<DocumentoElectronico | null>(null);
  const [cargando, setCargando] = useState(true);
  const [procesando, setProcesando] = useState(false);

  // Las ventas temporales son pruebas: no se emiten ni se consulta su documento.
  const esTemporal = String(numeroFactura ?? '').startsWith('TEMP-');

  const cargar = useCallback(async () => {
    if (esTemporal) { setCargando(false); return; }
    try {
      setCargando(true);
      const d = await FacturacionElectronicaApi.consultar(idVenta);
      setDoc(d);
      onCambio?.(d);
    } catch {
      // Silencioso: que la facturación electrónica no esté configurada no debe
      // romper la vista de la venta.
      setDoc(null);
    } finally {
      setCargando(false);
    }
  }, [idVenta, onCambio, esTemporal]);

  useEffect(() => { cargar(); }, [cargar]);

  const emitir = async () => {
    try {
      setProcesando(true);
      const d = await FacturacionElectronicaApi.emitir(idVenta);
      setDoc(d);
      onCambio?.(d);
      if (d.Estado === 'VALIDADO') toast.success(`Factura autorizada por la DIAN · ${d.Prefijo}${d.NumeroDocumento}`);
      else toast.error(d.MensajeError ?? 'La DIAN rechazó el documento');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'No se pudo emitir la factura');
    } finally {
      setProcesando(false);
    }
  };

  const reintentar = async () => {
    if (!doc) return;
    try {
      setProcesando(true);
      const d = await FacturacionElectronicaApi.reintentar(doc.Id);
      setDoc(d);
      onCambio?.(d);
      if (d.Estado === 'VALIDADO') toast.success('Factura autorizada por la DIAN');
      else toast.error(d.MensajeError ?? 'La DIAN volvió a rechazar el documento');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'No se pudo reintentar');
    } finally {
      setProcesando(false);
    }
  };

  if (esTemporal) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <ReceiptIcon fontSize="small" color="disabled" />
          <Typography variant="subtitle2" fontWeight={800}>Facturación electrónica</Typography>
          <Chip size="small" label="Venta temporal" variant="outlined" />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Esta venta es temporal (de prueba) y no se emite ante la DIAN.
          Regístrala como factura oficial si necesitas facturarla.
        </Typography>
      </Paper>
    );
  }

  if (cargando) {
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }

  const validado = doc?.Estado === 'VALIDADO';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <ReceiptIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={800} sx={{ flex: 1 }}>
          Facturación electrónica
        </Typography>
        {doc && (
          <Chip size="small" label={doc.Estado} color={COLOR_ESTADO[doc.Estado] ?? 'default'}
            icon={validado ? <CheckIcon /> : doc.Estado === 'RECHAZADO' || doc.Estado === 'ERROR' ? <ErrorIcon /> : undefined} />
        )}
      </Stack>

      {!doc ? (
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Esta venta aún no se ha enviado a la DIAN.
          </Typography>
          <Box>
            <Button variant="contained" size="small" startIcon={<SendIcon />}
              onClick={emitir} disabled={procesando}>
              {procesando ? 'Enviando…' : 'Emitir ante la DIAN'}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={1.2}>
          <Box>
            <Typography variant="caption" color="text.secondary">Número legal</Typography>
            <Typography variant="body2" fontWeight={700}>
              {doc.Prefijo}{doc.NumeroDocumento}
              {doc.NumeroResolucion && (
                <Typography component="span" variant="caption" color="text.secondary">
                  {' '}· Resolución {doc.NumeroResolucion}
                </Typography>
              )}
            </Typography>
          </Box>

          {doc.CUFE && (
            <Box>
              <Typography variant="caption" color="text.secondary">CUFE</Typography>
              <Tooltip title="Código Único de Factura Electrónica">
                <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.4 }}>
                  {cufeLegible(doc.CUFE)}
                </Typography>
              </Tooltip>
            </Box>
          )}

          {doc.MensajeError && (
            <Typography variant="caption" color="error.main">
              {doc.MensajeError}
            </Typography>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {doc.UrlPdf && (
              <Button size="small" variant="outlined" startIcon={<PdfIcon />}
                href={doc.UrlPdf} target="_blank" rel="noopener">PDF DIAN</Button>
            )}
            {doc.UrlXml && (
              <Button size="small" variant="outlined" startIcon={<XmlIcon />}
                href={doc.UrlXml} target="_blank" rel="noopener">XML</Button>
            )}
            {!validado && (
              <Button size="small" variant="contained" color="warning" startIcon={<RetryIcon />}
                onClick={reintentar} disabled={procesando}>
                {procesando ? 'Reintentando…' : `Reintentar (${doc.Intentos})`}
              </Button>
            )}
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

export default PanelDocumentoDian;
